import { Editor } from '@tiptap/core'

/**
 * Regex to match {{ variableName }} patterns
 * Matches: {{ variableName }}, {{variableName}}, {{ variable name }}, etc.
 * Compiled once for better performance
 */
const VARIABLE_REGEX = /\{\{\s*([^}]+?)\s*\}\}/g

/**
 * Extracts variables from HTML string(s)
 * Variables are in the format: {{ variableName }}
 * Variables are case-insensitive (NEW, New, new are treated as the same)
 */
function extractVariablesFromHTML(htmlStrings: string[]): string[] {
  // Use Map to store lowercase key -> original casing
  // This ensures case-insensitive uniqueness while preserving original casing
  const variablesMap = new Map<string, string>()

  for (const html of htmlStrings) {
    if (!html) continue

    // Reset regex lastIndex for each string to ensure proper matching
    VARIABLE_REGEX.lastIndex = 0
    let match: RegExpExecArray | null

    while ((match = VARIABLE_REGEX.exec(html)) !== null) {
      const variableName = match[1]?.trim()
      // Only process non-empty variable names
      if (variableName) {
        const lowercaseKey = variableName.toLowerCase()
        // Only add if we haven't seen this variable (case-insensitive) before
        // Use the first occurrence's casing
        if (!variablesMap.has(lowercaseKey)) {
          variablesMap.set(lowercaseKey, variableName)
        }
      }
    }
  }

  // Convert Map values to Array and sort alphabetically (case-insensitive)
  return Array.from(variablesMap.values()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
}

/**
 * Gets HTML content from all ProseMirror editors on the page
 * Caches the DOM query result to avoid repeated queries
 *
 * @param currentEditor - The current editor instance to exclude
 * @returns Object with current editor HTML and other editors HTML arrays
 */
function getAllEditorHTML(currentEditor: Editor): {
  currentEditorHTML: string
  otherEditorsHTML: string[]
} {
  const currentEditorHTML = currentEditor.getHTML()
  const otherEditorsHTML: string[] = []

  try {
    // Find all ProseMirror editors on the page
    // TipTap uses .ProseMirror class for editor content
    const allEditorElements = document.querySelectorAll('.ProseMirror')
    const currentEditorElement = currentEditor.view.dom

    for (const editorElement of allEditorElements) {
      // Skip the current editor's element to avoid duplicates
      if (editorElement === currentEditorElement) {
        continue
      }

      // Get HTML content from the editor element
      const html = editorElement.innerHTML
      if (html?.trim()) {
        otherEditorsHTML.push(html)
      }
    }
  } catch (error) {
    // If anything fails, return empty array (we'll still have the current editor's HTML)
    console.warn('Failed to extract HTML from all editors:', error)
  }

  return { currentEditorHTML, otherEditorsHTML }
}

/**
 * Extracts all unique variable names from the document
 * Variables are in the format: {{ variableName }}
 * Now extracts from all elements on the page, not just the current editor
 */
export function extractVariablesFromDocument(editor: Editor, additionalHTML?: string[]): string[] {
  if (!editor) {
    return []
  }

  const { currentEditorHTML, otherEditorsHTML } = getAllEditorHTML(editor)
  const htmlStrings = [currentEditorHTML, ...otherEditorsHTML]

  // Add any additional HTML content if provided
  if (additionalHTML?.length) {
    htmlStrings.push(...additionalHTML)
  }

  return extractVariablesFromHTML(htmlStrings)
}

/**
 * Extracts variables from other editors only (excluding the current editor)
 * This is used to check if a variable exists elsewhere when determining if it's "new"
 */
export function extractVariablesFromOtherEditors(editor: Editor, additionalHTML?: string[]): string[] {
  if (!editor) {
    return []
  }

  const { otherEditorsHTML } = getAllEditorHTML(editor)
  const htmlStrings = [...otherEditorsHTML]

  // Add any additional HTML content if provided
  if (additionalHTML?.length) {
    htmlStrings.push(...additionalHTML)
  }

  return extractVariablesFromHTML(htmlStrings)
}

/**
 * Extracts all unique variable names from the document and other editors in an optimized way.
 * This avoids duplicate DOM queries when both sets of variables are needed.
 *
 * @param editor - The current editor instance
 * @param additionalHTML - Optional additional HTML strings to include
 * @returns An object containing both all variables and variables from other editors
 */
export function extractVariablesOptimized(
  editor: Editor,
  additionalHTML?: string[]
): { allVariables: string[]; otherEditorsVariables: string[] } {
  if (!editor) {
    return { allVariables: [], otherEditorsVariables: [] }
  }

  const { currentEditorHTML, otherEditorsHTML } = getAllEditorHTML(editor)

  const allHtmlStrings = [currentEditorHTML, ...otherEditorsHTML]
  const otherEditorsHtmlStrings = [...otherEditorsHTML]

  if (additionalHTML?.length) {
    allHtmlStrings.push(...additionalHTML)
    otherEditorsHtmlStrings.push(...additionalHTML)
  }

  const allVariables = extractVariablesFromHTML(allHtmlStrings)
  const otherEditorsVariables = extractVariablesFromHTML(otherEditorsHtmlStrings)

  return { allVariables, otherEditorsVariables }
}

/**
 * Filters variables based on query string
 * Returns matching variables and optionally marks new variables
 *
 * Performance optimizations:
 * - Uses Set for O(1) lookup when checking if query exists
 * - Single pass filtering where possible
 * - Early returns for empty queries
 *
 * @param variables - All variables from the document (including current editor)
 * @param query - The query string being typed
 * @param variablesFromOtherEditors - Variables from other editors only (excluding current editor), used to check if variable is truly new
 * @returns Array of filtered variables with optional isNew flag
 */
export function filterVariables(
  variables: string[],
  query: string,
  variablesFromOtherEditors?: string[]
): Array<{ variable: string; isNew?: boolean }> {
  const trimmedQuery = query.trim()
  const lowercaseQuery = trimmedQuery.toLowerCase()

  // Early return for empty query
  if (!lowercaseQuery) {
    return variables.map(v => ({ variable: v }))
  }

  // Create a Set of lowercase variable names from other editors for O(1) lookup
  // This is used to quickly check if the query matches an existing variable elsewhere
  const otherEditorsLowercaseSet = variablesFromOtherEditors
    ? new Set(variablesFromOtherEditors.map(v => v.toLowerCase()))
    : new Set(variables.map(v => v.toLowerCase()))

  // Check if query exactly matches any existing variable in other editors
  // This determines if the typed value is truly new or matches an existing variable elsewhere
  const queryExistsExactly = otherEditorsLowercaseSet.has(lowercaseQuery)

  // Filter variables that match the query (case-insensitive partial match)
  // Build results array in a single pass
  const results: Array<{ variable: string; isNew?: boolean }> = []
  let queryFoundInResults = false

  for (const variable of variables) {
    const variableLower = variable.toLowerCase()

    // If variable partially matches the query, add it to results
    if (variableLower.includes(lowercaseQuery)) {
      // If this is an exact match and query doesn't exist in other editors, mark as new
      const isExactMatch = variableLower === lowercaseQuery
      const isNew = !queryExistsExactly && isExactMatch

      results.push({ variable, ...(isNew && { isNew: true }) })

      if (isExactMatch) {
        queryFoundInResults = true
      }
    }
  }

  // If query doesn't exactly match any existing variable in other editors, add it as new
  // (if it's not already in results from partial matching)
  if (!queryExistsExactly && !queryFoundInResults) {
    results.push({ variable: trimmedQuery, isNew: true })
  }

  return results
}
